import styled from 'styled-components';
import { colors } from '../../../../../config/styles';
import { Controller, useForm } from 'react-hook-form';
import { InputField } from '../../../../../components/Form';
import { isNil } from 'lodash';
import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/Button';
import { parseEmbedUrl } from '../../../../../components/ExternalEmbed';
import { EmbedType } from '../../../../../components/ExternalEmbed/types';
import { DashboardMediaPopoverMode, IDashboardMediaFormProps } from './types';
import { floatingComponentStyles } from '../../../../../styles/shareableStyles';

const Panel = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  width: 480px;
  ${floatingComponentStyles};
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ActionRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

const Error = styled.div`
  color: ${colors.mainError};
`;

const zEmbedUrl = z
  .string()
  .min(1, 'Please enter a link')
  .superRefine((arg, ctx) => {
    const parsedUrl = parseEmbedUrl(arg);
    if (parsedUrl.type === EmbedType.Unsupported) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The link is not supported',
        fatal: true,
      });

      return z.NEVER;
    }
  });

const formSchema = z.object({
  url: zEmbedUrl,
});

type IFormSchema = z.infer<typeof formSchema>;

export const DashboardMediaForm = ({
  mode,
  url,
  onSubmit,
  onCancel,
}: IDashboardMediaFormProps) => {
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<IFormSchema>({
    values: {
      url: url ?? '',
    },
    resolver: zodResolver(formSchema),
  });

  const submitHandler = handleSubmit((form) => {
    onSubmit(form.url);
  });

  return (
    <Panel>
      <Group>
        <Controller
          control={control}
          name="url"
          render={({ field, fieldState: { error } }) => (
            <InputField
              {...field}
              inputMode="url"
              autoFocus
              hasError={!isNil(error)}
              placeholder="Youtube and Loom share links are supported"
            />
          )}
        />
        {errors.url && <Error>{errors.url.message}</Error>}
      </Group>
      <ActionRow>
        <SecondaryButton
          size="small"
          onClick={onCancel}
          trackContext="dashboards"
          trackAction="cancel link"
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          size="small"
          onClick={submitHandler}
          trackContext="dashboards"
          trackAction={
            mode === DashboardMediaPopoverMode.Add ? 'add link' : 'update link'
          }
        >
          {mode === DashboardMediaPopoverMode.Add ? 'Add' : 'Update'}
        </PrimaryButton>
      </ActionRow>
    </Panel>
  );
};
